import styles from '../index.module.scss';
import { FormPassword, FormText } from '@components/form';
import { Text } from '@components/text';
import { Button, FormCheckboxWithLabel, medium_height, small_height, utility } from '@phi/components';
import { PropType, defineComponent, h, ref } from 'vue';
import { set } from '@phi/util/vuetils';
import { Email } from '@phi/newtype';
import { Option } from '@vaaas/fpts/option';
import { ErrorPrinter } from '../error-printer';
import { absorbed } from '@vaaas/fpts/dom';

export type FormData = {
    email: Email;
    password: string;
    newsletter: boolean;
}

const footer = h('footer', {
    innerHTML: `
        By proceeding you are agreeing to
        our <a href='https://phi3d.com/terms-of-service' target='_blank'>terms of service</a>
        and our <a href='https://phi3d.com/privacy-policy' target='_blank'>privacy policy</a>
    `,
    class: styles.faded,
});

export const RegisterForm = defineComponent({
    name: 'RegisterForm',

    props: {
        light: {
            required: false,
            type: Boolean,
            default: true,
        },

        error: {
            required: false,
            type: null as any as PropType<Option<Error>>,
        },

        disabled: {
            required: false,
            type: Boolean,
            default: false,
        },

        note: {
            required: false,
            type: String,
        },
    },

    emits: {
        login: () => true,
        onshape: () => true,
        register: (x: FormData) => true,
    },

    setup(props, { emit }) {
        const email = ref('');
        const password = ref('');
        const newsletter = ref(false);

        const formDataProvider = (): FormData => ({
            email: email.value as any as Email,
            password: password.value,
            newsletter: newsletter.value,
        });

        return () => h('form', {
            class: {
                [utility.margin_gap_2]: small_height(),
                [utility.margin_gap_3]: !small_height() && medium_height(),
                [utility.margin_gap_5]: !medium_height(),
            },
            onSubmit: absorbed(() => emit('register', formDataProvider())),
        }, [
            h('header', [
                h(Text, { size: small_height() ? 'bigger' : 'huge', class: styles.faded }, () => 'Create Account'),
                h('div', [
                    h('span', { class: styles.faded }, 'Already have a Phi or Onshape account? '),
                    h('a', {
                        href: '#',
                        class: [utility.underline, 'go-to-login-form'],
                        onClick: () => emit('login'),
                    }, 'Log In'),
                ]),
                h('br'),
            ]),

            //h('fieldset', LogInWithOnshape(props.light, () => emit('onshape'))),

            //h(SeparatorWithText, { class: styles.faded }, () => 'OR REGISTER'),

            h('fieldset', [
                h('label', { for: 'register-email' }, 'Email Address'),
                h(FormText, {
                    id: 'register-email',
                    name: 'email',
                    value: email.value,
                    'onUpdate:value': set(email),
                    type: 'email',
                    variant: 'outlined',
                    autocomplete: 'email',
                    disabled: props.disabled,
                }),
            ]),

            h('fieldset', [
                h('label', {
                    for: 'register-password',
                    class: [
                        utility.flex,
                        utility.space_between,
                    ],
                }, 'Password'),
                h(FormPassword, {
                    id: 'register-password',
                    name: 'password',
                    variant: 'outlined',
                    value: password.value,
                    'onUpdate:value': set(password),
                    disabled: props.disabled,
                }),
            ]),

            ErrorPrinter(props.error),
            props.note && h('p', props.note),

            h('fieldset', { class: [utility.flex, utility.gap_3, utility.align_center] },
                h(FormCheckboxWithLabel, {
                    value: newsletter.value,
                    'onUpdate:value': set(newsletter),
                }, () => 'Subscribe me to the newsletter'),
            ),

            h('fieldset', {
                class: {
                    [utility.margin_gap_1]: small_height(),
                    [utility.margin_gap_3]: !small_height(),
                },
            }, [
                h(Button, {
                    type: 'submit',
                    class: [ styles.height, 'sign-up' ],
                    variant: 'primary',
                    block: true,
                    disabled: props.disabled,
                }, () => h('span', { class: utility.bold }, 'Start Trial!')),

                footer,
            ]),
        ]);
    }
});
